import {
  Sensor,
  useHVACSensorsByGroup,
  useSeerRating,
  useHVACRawData,
  HVACRawDataType,
  POLLING_INTERVALS_MILLIS,
} from 'common';
import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XStack } from 'tamagui';

import { WidgetsWrapper } from '../../components/common';
import { DraggableWrapper } from '../../components/common/DraggableWrapper';
import { useWidgetOrder } from '../../hooks';
import { HVACEfficiencyContent } from './components/HVACEfficiencyContent';
import { HVACHumidityContent } from './components/HVACHumidityContent';
import { HVACRawDataContent } from './components/HVACRawDataContent';
import { HVACTemperatureContent } from './components/HVACTemperatureContent';
import {
  DraggableItemData,
  DraggableWidgetCard,
} from '../../components/common/DraggableWidgetCard';
import { useAppSync } from '../../contexts/AppSyncContext';

enum HVACWidgetCardTypes {
  HVACIndoorTemperature = 'HVACIndoorTemperature',
  HVACOutdoorTemperature = 'HVACOutdoorTemperature',
  HVACIndoorHumidity = 'HVACIndoorHumidity',
  HVACOutdoorHumidity = 'HVACOutdoorHumidity',
  HVACEfficiency = 'HVACEfficiency',
  HVACSeerRawData = 'HVACSeerRawData',
}

const renderContent = (
  type: HVACWidgetCardTypes,
  indoorSensor?: Sensor,
  outdoorSensor?: Sensor,
  seerRawData?: any[],
  seerLoading?: boolean,
) => {
  switch (type) {
    case HVACWidgetCardTypes.HVACIndoorTemperature:
      return (
        <HVACTemperatureContent
          key={HVACWidgetCardTypes.HVACIndoorTemperature}
          sensor={indoorSensor}
        />
      );
    case HVACWidgetCardTypes.HVACOutdoorTemperature:
      return (
        <HVACTemperatureContent
          key={HVACWidgetCardTypes.HVACOutdoorTemperature}
          sensor={outdoorSensor}
        />
      );
    case HVACWidgetCardTypes.HVACIndoorHumidity:
      return (
        <HVACHumidityContent key={HVACWidgetCardTypes.HVACIndoorHumidity} sensor={indoorSensor} />
      );
    case HVACWidgetCardTypes.HVACOutdoorHumidity:
      return (
        <HVACHumidityContent key={HVACWidgetCardTypes.HVACOutdoorHumidity} sensor={outdoorSensor} />
      );
    case HVACWidgetCardTypes.HVACEfficiency:
      return <HVACEfficiencyContent key={HVACWidgetCardTypes.HVACEfficiency} />;
    case HVACWidgetCardTypes.HVACSeerRawData:
      return (
        <HVACRawDataContent
          key={HVACWidgetCardTypes.HVACSeerRawData}
          data={seerRawData || []}
          isValidConfiguration={true}
          isLoading={seerLoading || false}
        />
      );
  }
};

const HVACContent: React.FC = () => {
  const { t } = useTranslation();
  const { selectedHubId } = useAppSync();
  const { hvacSensorGroups } = useHVACSensorsByGroup(selectedHubId ?? undefined);
  const { data: seerData, loading: seerLoading, fetchSeerRating } = useSeerRating();
  const hvacRawData = useHVACRawData(seerData).map((item) => ({
    ...item,
    name: (() => {
      switch (item.name) {
        case HVACRawDataType.AirHandler:
          return t('hvac.rawData.airHandler');
        case HVACRawDataType.HumiditySR:
          return t('hvac.rawData.humiditySR');
        case HVACRawDataType.FanSpeed:
          return t('hvac.rawData.fanSpeed');
        case HVACRawDataType.FanSpeedRaw:
          return t('hvac.rawData.fanSpeedRaw');
        case HVACRawDataType.CondensorAmperage:
          return t('hvac.rawData.condenserAmperage');
        case HVACRawDataType.airHandlerAmperage:
          return t('hvac.rawData.airHandlerAvgAmperage');
        case HVACRawDataType.TotalPower:
          return t('hvac.rawData.totalPower');
        case HVACRawDataType.TotalCooling:
          return t('hvac.rawData.totalCooling');
        case HVACRawDataType.InstantSeer:
          return t('hvac.rawData.instantSeer');
        default:
          return item.name;
      }
    })(),
  }));

  const firstGroup = hvacSensorGroups[0];
  const indoorSensor = firstGroup?.indoorSensor;
  const outdoorSensor = firstGroup?.outdoorSensor;

  useEffect(() => {
    if (selectedHubId) {
      fetchSeerRating(selectedHubId);

      const interval = setInterval(() => {
        fetchSeerRating(selectedHubId);
      }, POLLING_INTERVALS_MILLIS.HVAC);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [selectedHubId, fetchSeerRating]);

  const data: DraggableItemData<HVACWidgetCardTypes>[] = useMemo(
    () => [
      {
        id: '3',
        title: t('hvac.indoorThermostat'),
        type: HVACWidgetCardTypes.HVACIndoorTemperature,
      },
      {
        id: '4',
        title: t('hvac.outdoorThermostat'),
        type: HVACWidgetCardTypes.HVACOutdoorTemperature,
      },
      { id: '5', title: t('hvac.indoorHumidity'), type: HVACWidgetCardTypes.HVACIndoorHumidity },
      { id: '6', title: t('hvac.outdoorHumidity'), type: HVACWidgetCardTypes.HVACOutdoorHumidity },
      {
        id: '7',
        title: t('hvac.efficiency'),
        type: HVACWidgetCardTypes.HVACEfficiency,
      },
      {
        id: '8',
        title: t('hvac.rawData.title'),
        type: HVACWidgetCardTypes.HVACSeerRawData,
      },
    ],
    [t],
  );

  const {
    items,
    handleItemsReordered,
    handleStartDragging,
    handleSaveOrder,
    handleCancelOrder,
    isItemEnabled,
    setItemEnabled,
  } = useWidgetOrder<DraggableItemData<HVACWidgetCardTypes>>('hvacWidgetsOrder', data);

  return (
    <WidgetsWrapper
      onStartDragging={handleStartDragging}
      onSaveOrder={handleSaveOrder}
      onCancelOrder={handleCancelOrder}>
      <XStack flex={1}>
        <DraggableWrapper items={items} onItemsReordered={handleItemsReordered}>
          {items.map((item, index) => (
            <DraggableWidgetCard
              key={item.id}
              item={item}
              index={index}
              enabled={isItemEnabled(item.id)}
              onEnabledChange={(enabled) => setItemEnabled(item.id, enabled)}>
              {item.type &&
                renderContent(item.type, indoorSensor, outdoorSensor, hvacRawData, seerLoading)}
            </DraggableWidgetCard>
          ))}
        </DraggableWrapper>
      </XStack>
    </WidgetsWrapper>
  );
};

export const HVAC: React.FC = () => <HVACContent />;
